import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/inventoryApi";
import { useAuth } from "../contexts/AuthContext";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { CheckCircle, ArrowLeft, Plus, X, Gift, ShieldCheck, Wallet } from "lucide-react";
import { toast } from "sonner";
import PemilihCari from "../components/etams/PemilihCari";
import { keteranganCalonFa, calonBelumDipilih, PESAN_PILIH_DULU } from "../lib/calonFaWo";

// J3-PATH-LABEL-01 — kelayakan FA adalah KATEGORI, bukan STATUS: ia menjawab
// ATURAN MANA yang membuat sebuah FA layak jadi WO, bukan kondisinya. Palet A2
// punya lima tone dan tak satu pun berarti "FOC" atau "Special Customer", jadi
// memaksakannya akan membuat Regular dan FOC tak terbedakan. Pembedanya
// dipindah ke saluran lain: IKON, ditambah label teks yang sudah unik.
//
// Cincin badge SENGAJA tidak dipakai sebagai pembeda. Seluruh cincin A2 diukur
// 1,1-2,3 lawan ambang 3,0 (SC 1.4.11) — ia tidak sanggup membawa informasi.
// Outline di sini murni chrome netral; yang membedakan adalah ikon + teks.
const PATH_LABEL = {
  regular: { label: "Reguler (DP dikonfirmasi)", Icon: CheckCircle },
  foc: { label: "FOC disetujui", Icon: Gift },
  special_customer: { label: "Special Customer", Icon: ShieldCheck },
  receivables_deferred: { label: "Piutang (disetujui Owner)", Icon: Wallet },
};

/** Chip kelayakan FA. Path yang tidak dikenal tampil apa adanya tanpa ikon —
 *  jalur itu belum bisa tercapai hari ini (wo_service.py:111 mengunci empat
 *  kode yang sama persis), tapi bentuk lamanya menyisakan teks nyaris tak
 *  terlihat kalau path kelima lahir. */
function PathBadge({ path }) {
  const p = PATH_LABEL[path];
  const Icon = p?.Icon;
  return (
    <Badge variant="outline" className="gap-1">
      {Icon && <Icon size={12} aria-hidden="true" />}
      {p?.label || path}
    </Badge>
  );
}

export default function WOCreate() {
  const navigate = useNavigate();
  const { profile } = useAuth();
  // T15 (Titipan auditor 26 Okt 2026): cabang WO TERKUNCI hanya untuk akun yang memang terikat satu cabang
  // (termasuk sesi atas-nama CS/Admin yang kini membawa cabang). Owner & Kepala Cabang: bawaan = cabang FA
  // pertama yang ditambahkan; bila belum terbaca, wajib dipilih — dulu diam-diam memakai cabang pertama di
  // daftar (Balikpapan) dan menguncinya.
  const cabangTerkunci = !!profile?.store_id;
  const [step, setStep] = useState(1);
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState(profile?.store_id || "");
  // T36: FA dipilih dari daftar FA berjalan di wilayah/cabang pengguna (GET /wo/calon-fa), bukan diketik.
  const [faPilih, setFaPilih] = useState(null);
  const [checking, setChecking] = useState(false);
  const [selectedFAs, setSelectedFAs] = useState([]);  // [{id, fa_number, path, reason}]
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/stores").then(r => {
      const list = r.data || [];
      setStores(list);
      if (!storeId && profile?.store_id) setStoreId(profile.store_id);
    }).catch((e) => toast.error(apiErrorMessage(e, "Daftar cabang gagal dimuat.")));
  }, []); // eslint-disable-line

  const cariCalon = async (kata) => {
    const r = await api.get("/wo/calon-fa", { params: { q: kata || undefined, store_id: storeId || undefined } });
    return calonBelumDipilih(r.data?.items, selectedFAs);
  };

  const _addFA = async (pilihan = faPilih) => {
    // T36: Tambah tanpa pilihan → penjelasan, bukan tombol mati tanpa sebab.
    if (!pilihan?.id) { setError(PESAN_PILIH_DULU); return; }
    const input = pilihan.fa_number || "";
    const faId = pilihan.id;
    setChecking(true); setError("");
    try {
      if (selectedFAs.find(x => x.id === faId)) {
        setError("FA sudah ditambahkan.");
        setChecking(false); return;
      }
      const r = await api.get(`/wo/_diag/eligibility/${faId}`);
      const e = r.data;
      if (!e.eligible) {
        setError(e.reason || "FA tidak layak.");
        setChecking(false); return;
      }
      if (!storeId && e.fa?.store_id) setStoreId(e.fa.store_id);
      setSelectedFAs(prev => [...prev, {
        id: faId,
        fa_number: e.fa?.fa_number || input,
        // K233 (BUSINESS-111): nama tim dari daftar pilihan (server mengirimnya hanya ke peran yang boleh).
        nama_team: pilihan.nama_team || null,
        path: e.path,
        reason: e.reason,
        store_id: e.fa?.store_id,
        region_id: e.fa?.region_id,
      }]);
      setFaPilih(null);
    } catch (err) {
      setError(apiErrorMessage(err, "Kelayakan FA gagal diperiksa."));
    } finally {
      setChecking(false);
    }
  };

  const _removeFA = (id) => setSelectedFAs(s => s.filter(x => x.id !== id));

  const _submit = async () => {
    setCreating(true); setError("");
    try {
      const r = await api.post("/wo", {
        source_fa_ids: selectedFAs.map(f => f.id),
        store_id: storeId,
        notes: notes || null,
      });
      setResult(r.data);
      setStep(3);
    } catch (err) {
      const msg = apiErrorMessage(err, "Gagal membuat WO.");
      setError(msg);
      toast.error(msg);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl mx-auto" data-testid="wo-create-page">
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={() => navigate("/wo")}><ArrowLeft className="h-4 w-4 mr-1" /> Kembali</Button>
        <h1 className="text-2xl font-semibold">Buat WO Baru</h1>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 text-sm">
        {[1, 2, 3].map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 ${step >= s ? "text-primary font-medium" : "text-muted-foreground"}`}>
              {/* KONTRAS-01: lingkaran langkah AKTIF sengaja TIDAK disentuh —
                  itu pasangan warna merek di atas chip berwarna, bukan soal
                  kontras tema. Yang sudah diganti adalah langkah TIDAK aktif:
                  dulu lingkaran abu terang dari palet keras yang tidak ikut
                  bertukar, sementara angkanya mewarisi warna teks tema —
                  di mode gelap jadi terang di atas terang.

                  Nama kelas warnanya SENGAJA tidak ditulis di sini: pemindai
                  Tailwind membaca teks berkas mentah dan tidak membedakan kode
                  dari komentar, jadi menyebutnya akan menghidupkan kembali
                  aturan CSS-nya di bundle produksi (J3-SAPUAN-METODE-01). */}
              <span className={`inline-flex h-6 w-6 rounded-full items-center justify-center text-xs ${step >= s ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>{s}</span>
              {s === 1 ? "Pilih FA" : s === 2 ? "Periksa" : "Hasil"}
            </div>
            {i < 2 && <span className="text-muted-foreground">›</span>}
          </React.Fragment>
        ))}
      </div>

      {step === 1 && (
        <Card className="p-6 space-y-4">
          <h2 className="font-medium">Langkah 1 — Pilih FA</h2>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Cabang WO</label>
            <Select value={storeId} onValueChange={setStoreId} disabled={cabangTerkunci}>
              <SelectTrigger data-testid="wo-create-store"><SelectValue placeholder="Pilih cabang..." /></SelectTrigger>
              <SelectContent>{stores.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1" data-testid="wo-create-store-hint">
              {cabangTerkunci
                ? "Cabang mengikuti cabang akun Anda."
                : storeId
                  ? "Cabang mengikuti FA pertama. Ganti hanya bila memang diproduksi di cabang lain."
                  : "Cabang terisi otomatis dari FA pertama yang ditambahkan, atau pilih sendiri."}
            </p>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Tambahkan FA</label>
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <PemilihCari
                  key={`${storeId}-${selectedFAs.length}`}
                  value={faPilih}
                  onChange={(f) => { setFaPilih(f); setError(""); }}
                  cari={cariCalon}
                  label={(f) => f?.fa_number || ""}
                  keterangan={keteranganCalonFa}
                  placeholder="Pilih FA yang siap masuk WO…"
                  placeholderCari="Ketik nomor FA atau nama tim…"
                  minHuruf={1}
                  tampilAwal
                  batas={30}
                  testId="wo-fa-pilih"
                />
              </div>
              <Button onClick={() => _addFA()} disabled={checking} data-testid="wo-fa-add-btn">
                <Plus className="h-4 w-4 mr-1" /> {checking ? "Cek…" : "Tambah"}
              </Button>
            </div>
            {error && <p className="text-xs text-danger-soft-foreground mt-2" data-testid="wo-fa-error">{error}</p>}
            <p className="text-xs text-muted-foreground mt-1">
              Daftar hanya berisi FA {storeId ? "cabang ini" : "wilayah Anda"} yang belum masuk WO. Kelayakan tetap
              diperiksa saat ditambahkan (uang muka sudah dikonfirmasi / FOC / Special Customer / piutang disetujui Owner).
            </p>
          </div>

          {selectedFAs.length > 0 && (
            <div className="border rounded p-3 space-y-2 bg-muted/50">
              <p className="text-xs font-medium text-foreground">FA terpilih ({selectedFAs.length})</p>
              {selectedFAs.map((f) => (
                <div key={f.id} className="flex items-center justify-between bg-card rounded px-3 py-2 border">
                  <div className="flex flex-col">
                    <span className="font-mono text-xs">{f.fa_number}{f.nama_team && <span className="ml-2 font-sans text-foreground" data-testid="wo-fa-nama-tim">{f.nama_team}</span>}</span>
                    <span className="text-xs text-muted-foreground">{f.reason}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <PathBadge path={f.path} />
                    <Button size="sm" variant="ghost" onClick={() => _removeFA(f.id)}><X className="h-4 w-4" /></Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end">
            <Button
              disabled={selectedFAs.length === 0 || !storeId}
              onClick={() => setStep(2)}
              data-testid="wo-step1-next"
            >Lanjut Periksa →</Button>
          </div>
        </Card>
      )}

      {step === 2 && (
        <Card className="p-6 space-y-4">
          <h2 className="font-medium">Langkah 2 — Periksa</h2>
          <div className="space-y-2 text-sm">
            <p><span className="text-muted-foreground">Cabang:</span> {stores.find(s => s.id === storeId)?.name || "-"}</p>
            <p className="text-muted-foreground">FA sumber:</p>
            <ul className="list-disc list-inside">
              {selectedFAs.map(f => <li key={f.id}><span className="font-mono">{f.fa_number}</span> — <PathBadge path={f.path} /></li>)}
            </ul>
          </div>
          <div>
            <label className="text-xs text-muted-foreground mb-1 block">Catatan (opsional)</label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} data-testid="wo-notes" />
          </div>
          {error && <p className="text-xs text-danger-soft-foreground" data-testid="wo-create-error">{error}</p>}
          <div className="flex justify-between">
            <Button variant="outline" onClick={() => setStep(1)}>← Kembali</Button>
            <Button disabled={creating} onClick={_submit} data-testid="wo-step2-submit">
              {creating ? "Membuat WO…" : "Konfirmasi & Buat WO"}
            </Button>
          </div>
        </Card>
      )}

      {step === 3 && result && (
        <Card className="p-6 space-y-4 border-success-soft-border bg-success-soft">
          <div className="flex items-center gap-2 text-success-soft-foreground">
            <CheckCircle className="h-6 w-6" />
            <h2 className="font-medium">WO Berhasil Dibuat</h2>
          </div>
          <p className="text-sm">
            <span className="font-mono font-medium">{result.wo_number}</span> tercatat dengan status <Badge>Draf</Badge>.
            {" "}Jalur kelayakan: {(result.eligibility_paths || []).map(p => PATH_LABEL[p]?.label || p).join(", ")}
          </p>
          <div className="flex gap-2">
            <Button onClick={() => navigate(`/wo/${result.id}`)} data-testid="wo-result-detail-btn">Lihat Detail</Button>
            <Button variant="outline" onClick={() => navigate("/wo")}>Kembali ke Daftar</Button>
          </div>
        </Card>
      )}
    </div>
  );
}
